import { filter, get, map, some, sortBy } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import ActionRow from './ActionRow';
import { ActionType, AnswerType, SendReplyAction as SendReplyActionType } from 'src/actions/rules/actions';
import answerSearchResource from 'src/actions/answers/answerSearchResource';
import answerVariablesResource from 'src/actions/answers/answerVariablesResource';
import AsyncDropdown from 'src/presentation/components/forms/AsyncDropdown';
import { default as Languages } from '@ad-scripts/domain/models/languages/answer_languages';
import {
  StyledDropdown,
  StyledTypeDescription,
  StyledTooltip,
  StyledInformationIcon,
} from 'src/presentation/components/pages/rules/shared/StyledComponents';
import useCurrentAnswer from 'src/presentation/components/pages/shared/hooks/useCurrentAnswer';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';

type SendReplyActionProps = {
  action: SendReplyActionType;
  onDelete: Function;
  onUpdate: Function;
};

const SendReplyAction: FC<SendReplyActionProps> = ({ action, onDelete, onUpdate }) => {
  const dispatch = useDispatch();
  const searchForAnswer = answerSearchResource.effects.request;

  const { loadingState, value: currentAnswer } = useCurrentAnswer(action.snippetId);
  const answerOptions = useAnswerOptions(currentAnswer);
  const availableLanguageOptions = useAnswerLanguageOptions(currentAnswer);
  const availableChannelOptions = useAvailableChannelOptions({
    answer: currentAnswer,
    language: action.snippetLanguage,
  });

  const onSearchAnswers = useCallback(
    text => {
      dispatch(searchForAnswer({ text }));
    },
    [dispatch, searchForAnswer]
  );

  const onChangeId = useCallback(
    id => {
      action.updateSnippetId(id);
      action.updateSnippetType();
      action.updateSnippetLanguage();
      onUpdate(ActionType.SendReply, action);
    },
    [action, onUpdate]
  );

  const onChangeType = useCallback(
    type => {
      action.updateSnippetType(type);
      onUpdate(ActionType.SendReply, action);
    },
    [action, onUpdate]
  );

  const onChangeLanguage = useCallback(
    lng => {
      action.updateSnippetLanguage(lng);
      action.updateSnippetType();
      onUpdate(ActionType.SendReply, action);
    },
    [action, onUpdate]
  );

  if (currentAnswer && isLoadingStatus(loadingState)) {
    return null;
  }

  return (
    <SendReplyRow type={ActionType.SendReply} onDelete={onDelete}>
      <div>
        <Row>
          <StyledTypeDescription>Send Auto-Reply</StyledTypeDescription>
          <StyledAnswerSearchDropdown
            data-aid="sendReply-selectAnswer-selectButton"
            maxRows={5}
            value={action.snippetId}
            options={answerOptions}
            onChange={onChangeId}
            onSearch={onSearchAnswers}
            placeholder="Search for answer"
          />
          <div>
            <StyledTooltip message="Auto replies can be sent for Email and all Messaging channels">
              <StyledInformationIcon />
            </StyledTooltip>
          </div>
        </Row>
        {!currentAnswer ? null : (
          <Row>
            <StyledDropdown
              data-aid="sendReply-selectLanguage-selectButton"
              rightMargin
              maxRows={5}
              onSelect={onChangeLanguage}
              options={availableLanguageOptions}
              placeholder="Select language"
              searchable
              value={action.snippetLanguage}
            />
            {availableChannelOptions.length === 0 ? null : (
              <React.Fragment>
                with
                <StyledDropdown
                  data-aid="sendReply-selectChannel-selectButton"
                  leftMargin
                  maxRows={5}
                  onSelect={onChangeType}
                  options={availableChannelOptions}
                  placeholder="Select channel"
                  value={action.snippetType}
                />
              </React.Fragment>
            )}
          </Row>
        )}
      </div>
    </SendReplyRow>
  );
};

function useAnswerLanguageOptions(answer) {
  const { values: answerVariables } = useResource(answerVariablesResource, { fetch: false });
  const disallowedAnswerVariableIds = useDisallowedVariableIds(answerVariables);

  if (!answer || !answer.contents) return [];

  return sortBy(
    map(
      filter(answer.contents, c => {
        return isValidAutoReplyContent(c, disallowedAnswerVariableIds);
      }),
      c => {
        const languageCode = c.language;
        return { value: languageCode, label: Languages[languageCode] || '' };
      }
    ),
    ['label']
  );
}

function useAvailableChannelOptions({ answer, language }) {
  const { values: answerVariables } = useResource(answerVariablesResource, { fetch: false });
  const disallowedAnswerVariableIds = useDisallowedVariableIds(answerVariables);

  if (!answer || !answer.contents || !language) return [];

  const content = answer.contents.find(c => c.language === language);
  if (!content) return [];

  return channelOptions.filter(({ value }) => {
    // adjust casing of enum to match that of attr name
    const channelKey = value[0].toLowerCase() + value.slice(1);
    return (
      !!content[channelKey] &&
      areVariablesSafeForRules(
        getVariableIdsFromString(get(content[channelKey], 'bodyHtml')),
        disallowedAnswerVariableIds
      )
    );
  });
}

function useDisallowedVariableIds(answerVariables) {
  return useMemo(() => {
    return new Set(answerVariables.filter(v => /^AGENT_/.test(v.type) || !v.defaultValue).map(v => v.id));
  }, [answerVariables]);
}

function useAnswerOptions(currentAnswer) {
  const { value: answerSearchResults } = useDocumentResource(answerSearchResource, { fetch: false });
  let containsCurrentAnswer = !(currentAnswer && currentAnswer.id);

  const { values: answerVariables } = useResource(answerVariablesResource, { fetch: false });

  const disallowedAnswerVariableIds = useDisallowedVariableIds(answerVariables);

  const answerOptions = answerSearchResults
    .filter(answer => {
      // if any content (i.e. languages) has a valid channel, show the answer
      return some(answer.contents, content => {
        return isValidAutoReplyContent(content, disallowedAnswerVariableIds);
      });
    })
    .map(answer => {
      if (!containsCurrentAnswer && answer.id === currentAnswer.id) {
        containsCurrentAnswer = true;
      }

      return {
        label: answer.name,
        value: answer.id,
      };
    });

  if (!containsCurrentAnswer) {
    answerOptions.push({ label: currentAnswer.name, value: currentAnswer.id });
  }

  return answerOptions;
}

function isValidAutoReplyContent(content, disallowedAnswerVariableIds) {
  const anyChannelBody = getBodyByChannelType(content, AnswerType.AnyChannel);
  const messageChannelBody = getBodyByChannelType(content, AnswerType.Message);

  if (anyChannelBody === undefined && messageChannelBody === undefined) {
    // must have one of AnyChannel or Message
    return false;
  }

  // only filter out if there are invalid variables in *both* AnyChannel and Message content
  // otherwise, give the user the opportunity to pick this answer, but ensure they select it w/ valid channel
  const anyChannelVariablesSafe =
    areVariablesSafeForRules(getVariableIdsFromString(anyChannelBody), disallowedAnswerVariableIds) &&
    areVariablesSafeForRules(getVariableIdsFromString(get(content, 'anyChannel.subject')), disallowedAnswerVariableIds);
  const messageChannelVariablesSafe = areVariablesSafeForRules(
    getVariableIdsFromString(messageChannelBody),
    disallowedAnswerVariableIds
  );

  if (anyChannelBody && messageChannelBody) {
    return anyChannelVariablesSafe || messageChannelVariablesSafe;
  }

  if (anyChannelBody) {
    return anyChannelVariablesSafe;
  }

  return messageChannelVariablesSafe;
}

function getBodyByChannelType(content, channel) {
  return get(content, `${channel}.bodyHtml`);
}

function getVariableIdsFromString(str) {
  let variableIds: string[] = [];
  let match;
  const re = /<variable data-id="([^"]+)">/g;
  while ((match = re.exec(str))) {
    const id = match[1];
    if (!variableIds.includes(id)) {
      variableIds = variableIds.concat(id);
    }
  }
  return variableIds;
}

function areVariablesSafeForRules(variableIdsToCheck, disallowedVariablesSet) {
  for (const variableId of variableIdsToCheck) {
    if (disallowedVariablesSet.has(variableId)) {
      return false;
    }
  }

  return true;
}

const SendReplyRow = styled(ActionRow)`
  align-items: baseline;
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledAnswerSearchDropdown = styled(AsyncDropdown)`
  margin-right: 12px;
  width: 220px;
`;

const channelOptions: { label: string; value: string }[] = [
  { label: 'Email', value: AnswerType.AnyChannel },
  { label: 'Messaging', value: AnswerType.Message },
];

export default SendReplyAction;
